/**
 * Catalogue de construction et paramètres d'équilibrage.
 *
 * Partagé : le client affiche coûts et effets dans le mode construction, le
 * serveur applique exactement les mêmes valeurs. Montants en euros, durées de
 * construction en heures de jeu.
 */

export type LiftKind = 'teleski' | 'telesiege' | 'telecabine';

export type BuildingKind =
  | 'chalet_accueil'
  | 'restaurant'
  | 'shop_rental'
  | 'ticket_office'
  | 'welcome_area'
  | 'snow_cannon'
  | 'lamp_post'
  | 'hotel'
  | 'residence'
  | 'ski_school'
  | 'first_aid'
  | 'snack_bar'
  | 'groomer_garage'
  | 'parking'
  | 'reservoir';

/** Rubrique du menu de construction. */
export type BuildingGroup = 'accueil' | 'hebergement' | 'restauration' | 'services' | 'exploitation';

/** Libellés des rubriques, dans l'ordre d'affichage du menu de construction. */
export const BUILDING_GROUPS: Record<BuildingGroup, string> = {
  accueil: 'Accueil et stationnement',
  hebergement: 'Hébergement',
  restauration: 'Restauration',
  services: 'Services aux skieurs',
  exploitation: 'Exploitation du domaine',
};

export type SupplyKind = 'food' | 'fuel';

export interface LiftSpec {
  kind: LiftKind;
  label: string;
  /** Nom court affiché devant le nom propre ("Télésiège des Crêtes"). */
  prefix: string;
  costBase: number;
  costPerMeter: number;
  /** Personnel et maintenance par jour d'ouverture. */
  staffPerDay: number;
  buildHours: number;
  /** Vitesse du câble (m/s). */
  speed: number;
  /** Intervalle entre deux véhicules (s). */
  interval: number;
  /** Places par véhicule. */
  seats: number;
  minLength: number;
  maxLength: number;
  /** Pente moyenne maximale de la ligne. */
  maxSlope: number;
  /** Vent au-delà duquel la remontée ferme (km/h). */
  windLimit: number;
  pylonSpacing: number;
  /** Hauteur du câble au-dessus du sol aux pylônes (m). */
  pylonCable: number;
  /** Hauteur du câble aux gares (m). */
  stationCable: number;
  /** Recul de l'ancrage câble depuis le centre de la gare (m). */
  stationAnchor: number;
  /** Décalage latéral des brins montant/descendant (m). */
  cableOffset: number;
  powerKw: number;
  /** Probabilité de panne par heure de fonctionnement (usure moyenne). */
  breakdownPerHour: number;
  repairCost: number;
  repairHours: number;
  assets: { station: string; pylon: string; carrier: string };
}

export const LIFTS: Record<LiftKind, LiftSpec> = {
  teleski: {
    kind: 'teleski',
    label: 'Téléski',
    prefix: 'Téléski',
    costBase: 40_000,
    costPerMeter: 45,
    staffPerDay: 700,
    buildHours: 6,
    speed: 3,
    interval: 4,
    seats: 1,
    minLength: 150,
    maxLength: 800,
    maxSlope: 0.45,
    windLimit: 90,
    pylonSpacing: 70,
    pylonCable: 6.5,
    stationCable: 3.5,
    stationAnchor: 2.5,
    cableOffset: 1.2,
    powerKw: 60,
    breakdownPerHour: 0.01,
    repairCost: 2_500,
    repairHours: 1.5,
    assets: { station: 'drag_station', pylon: 'drag_pylon', carrier: 'drag_hanger' },
  },
  telesiege: {
    kind: 'telesiege',
    label: 'Télésiège 4 places',
    prefix: 'Télésiège',
    costBase: 160_000,
    costPerMeter: 140,
    staffPerDay: 2_400,
    buildHours: 16,
    speed: 5,
    interval: 6.5,
    seats: 4,
    minLength: 300,
    maxLength: 1600,
    maxSlope: 0.7,
    windLimit: 65,
    pylonSpacing: 110,
    pylonCable: 11.5,
    stationCable: 5,
    stationAnchor: 6,
    cableOffset: 2.5,
    powerKw: 260,
    breakdownPerHour: 0.012,
    repairCost: 9_000,
    repairHours: 3,
    assets: { station: 'lift_station', pylon: 'pylon', carrier: 'chair_4' },
  },
  telecabine: {
    kind: 'telecabine',
    label: 'Télécabine 8 places',
    prefix: 'Télécabine',
    costBase: 380_000,
    costPerMeter: 260,
    staffPerDay: 4_200,
    buildHours: 28,
    speed: 6,
    interval: 10,
    seats: 8,
    minLength: 500,
    maxLength: 2200,
    maxSlope: 0.8,
    windLimit: 80,
    pylonSpacing: 160,
    pylonCable: 11.5,
    stationCable: 5,
    stationAnchor: 6,
    cableOffset: 2.5,
    powerKw: 420,
    breakdownPerHour: 0.008,
    repairCost: 14_000,
    repairHours: 4,
    assets: { station: 'lift_station', pylon: 'pylon', carrier: 'gondola_cabin' },
  },
};

export interface BuildingSpec {
  kind: BuildingKind;
  label: string;
  description: string;
  cost: number;
  upkeepPerDay: number;
  buildHours: number;
  /** Emprise au sol (m), largeur selon x local, profondeur selon z local. */
  footprint: [number, number];
  maxSlope: number;
  /** Où l'on peut construire. */
  zone: 'village' | 'domain' | 'piste-side' | 'anywhere';
  /** Rubrique du menu de construction. */
  group: BuildingGroup;
  asset: string;
  /** Visiteurs pouvant stationner simultanément (parking, accueil). */
  parking?: number;
  /** Forfaits vendus par heure de jeu. */
  ticketsPerHour?: number;
  /** Places assises de restauration. */
  seats?: number;
  /** Prix moyen d'un repas. */
  mealPrice?: number;
  /** Parc de matériel : paires de skis louables par jour (rendues le soir). */
  rentalsPerDay?: number;
  /** Clients équipés par heure de jeu au comptoir de location. */
  rentalsPerHour?: number;
  /** Bonus de satisfaction (points) apporté à l'ambiance. */
  ambiance?: number;
  /** Canon à neige : débit d'eau (m3 par heure de jeu). */
  waterPerHour?: number;
  /** Hébergement : lits occupés chaque nuit selon le taux d'occupation (clients déjà sur place le matin). */
  beds?: number;
  /** Hébergement : prix moyen d'une nuitée par lit occupé. */
  roomPrice?: number;
  /** École de ski : cours collectifs vendus par jour. */
  lessonsPerDay?: number;
  /** École de ski : prix d'un cours. */
  lessonPrice?: number;
  /** Poste de secours : évacuations d'une remontée moins pénibles pour les visiteurs. */
  rescue?: boolean;
  /** Garage à dameuses : dameuses abritées, qui sortent chaque nuit damer les pistes. */
  groomers?: number;
  /** Retenue collinaire : capacité d'eau ajoutée (m3). */
  waterCapacity?: number;
  /** Retenue collinaire : apport naturel ajouté (m3 par heure de jeu). */
  waterInflowPerHour?: number;
}

export const BUILDINGS: Record<BuildingKind, BuildingSpec> = {
  chalet_accueil: {
    kind: 'chalet_accueil',
    label: "Chalet d'accueil",
    description: 'Accueil de 300 visiteurs, caisses (200 forfaits par heure) et 60 places de restaurant.',
    cost: 160_000,
    upkeepPerDay: 1_400,
    buildHours: 10,
    footprint: [26, 14],
    maxSlope: 0.18,
    zone: 'village',
    group: 'accueil',
    asset: 'chalet_accueil',
    parking: 300,
    ticketsPerHour: 200,
    seats: 60,
    mealPrice: 14,
    ambiance: 2,
  },
  restaurant: {
    kind: 'restaurant',
    label: "Restaurant d'altitude",
    description: '80 places de restaurant sur le domaine, au départ ou à l\u2019arrivée des remontées.',
    cost: 90_000,
    upkeepPerDay: 900,
    buildHours: 8,
    footprint: [18, 12],
    maxSlope: 0.25,
    zone: 'domain',
    group: 'restauration',
    asset: 'restaurant',
    seats: 80,
    mealPrice: 18,
    ambiance: 3,
  },
  shop_rental: {
    kind: 'shop_rental',
    label: 'Location de skis',
    description: 'Location de matériel : parc de 400 paires, 90 clients équipés par heure.',
    cost: 45_000,
    upkeepPerDay: 500,
    buildHours: 5,
    footprint: [10, 8],
    maxSlope: 0.2,
    zone: 'village',
    group: 'services',
    asset: 'shop_rental',
    rentalsPerDay: 400,
    rentalsPerHour: 90,
    ambiance: 1,
  },
  ticket_office: {
    kind: 'ticket_office',
    label: 'Caisses',
    description: 'Caisses supplémentaires : 250 forfaits par heure.',
    cost: 22_000,
    upkeepPerDay: 350,
    buildHours: 3,
    footprint: [6, 4],
    maxSlope: 0.25,
    zone: 'village',
    group: 'accueil',
    asset: 'ticket_office',
    ticketsPerHour: 250,
  },
  welcome_area: {
    kind: 'welcome_area',
    label: "Zone d'accueil",
    description: 'Parking, navettes et portique : 350 visiteurs simultanés de plus.',
    cost: 30_000,
    upkeepPerDay: 200,
    buildHours: 4,
    footprint: [20, 12],
    maxSlope: 0.2,
    zone: 'village',
    group: 'accueil',
    asset: 'welcome_area',
    parking: 350,
    ambiance: 2,
  },
  snow_cannon: {
    kind: 'snow_cannon',
    label: 'Enneigeur',
    description: 'Neige de culture sur la piste la plus proche (froid requis, consomme de l\u2019eau).',
    cost: 9_000,
    upkeepPerDay: 60,
    buildHours: 1,
    footprint: [3, 3],
    maxSlope: 0.9,
    zone: 'piste-side',
    group: 'exploitation',
    asset: 'snow_cannon',
    waterPerHour: 30,
  },
  lamp_post: {
    kind: 'lamp_post',
    label: 'Lampadaire',
    description: 'Éclairage et ambiance du village.',
    cost: 1_200,
    upkeepPerDay: 5,
    buildHours: 0.5,
    footprint: [1, 1],
    maxSlope: 0.6,
    zone: 'anywhere',
    group: 'exploitation',
    asset: 'lamp_post',
    ambiance: 0.2,
  },
  hotel: {
    kind: 'hotel',
    label: 'Hôtel',
    description: 'Clientèle à la semaine : nuitées facturées et skieurs déjà sur place.',
    cost: 420_000,
    upkeepPerDay: 2_600,
    buildHours: 30,
    footprint: [30, 16],
    maxSlope: 0.15,
    zone: 'village',
    group: 'hebergement',
    asset: 'hotel',
    beds: 120,
    roomPrice: 95,
    ambiance: 3,
  },
  residence: {
    kind: 'residence',
    label: 'Résidence de tourisme',
    description: 'Beaucoup de lits à prix modeste : plus de skieurs chaque matin.',
    cost: 380_000,
    upkeepPerDay: 1_500,
    buildHours: 26,
    footprint: [34, 14],
    maxSlope: 0.15,
    zone: 'village',
    group: 'hebergement',
    asset: 'residence',
    beds: 220,
    roomPrice: 38,
    ambiance: 0.5,
  },
  ski_school: {
    kind: 'ski_school',
    label: 'École de ski',
    description: 'Cours collectifs : les débutants paient un cours et restent plus contents.',
    cost: 70_000,
    upkeepPerDay: 900,
    buildHours: 6,
    footprint: [24, 14],
    maxSlope: 0.2,
    zone: 'village',
    group: 'services',
    asset: 'ski_school',
    lessonsPerDay: 160,
    lessonPrice: 42,
    ambiance: 1,
  },
  first_aid: {
    kind: 'first_aid',
    label: 'Poste de secours',
    description: 'Pisteurs secouristes : évacuations moins pénibles pour les visiteurs.',
    cost: 55_000,
    upkeepPerDay: 700,
    buildHours: 4,
    footprint: [10, 8],
    maxSlope: 0.3,
    zone: 'domain',
    group: 'services',
    asset: 'first_aid',
    rescue: true,
    ambiance: 1,
  },
  snack_bar: {
    kind: 'snack_bar',
    label: "Snack d'altitude",
    description: 'Petite restauration rapide et transats, au départ ou à l\u2019arrivée des remontées.',
    cost: 38_000,
    upkeepPerDay: 380,
    buildHours: 3,
    footprint: [12, 10],
    maxSlope: 0.3,
    zone: 'domain',
    group: 'restauration',
    asset: 'snack_bar',
    seats: 34,
    mealPrice: 12,
    ambiance: 1.5,
  },
  groomer_garage: {
    kind: 'groomer_garage',
    label: 'Garage à dameuses',
    description: 'Trois dameuses qui sortent chaque nuit damer les pistes du plan de damage.',
    cost: 240_000,
    upkeepPerDay: 1_100,
    buildHours: 12,
    footprint: [24, 16],
    maxSlope: 0.15,
    zone: 'domain',
    group: 'exploitation',
    asset: 'groomer_garage',
    groomers: 3,
  },
  parking: {
    kind: 'parking',
    label: 'Parking',
    description: '420 places pour les visiteurs à la journée.',
    cost: 28_000,
    upkeepPerDay: 120,
    buildHours: 3,
    footprint: [48, 30],
    maxSlope: 0.12,
    zone: 'village',
    group: 'accueil',
    asset: 'parking',
    parking: 420,
  },
  reservoir: {
    kind: 'reservoir',
    label: 'Retenue collinaire',
    description: 'Plus d\u2019eau stockée et un meilleur remplissage pour la neige de culture.',
    cost: 210_000,
    upkeepPerDay: 300,
    buildHours: 20,
    footprint: [64, 44],
    maxSlope: 0.12,
    zone: 'anywhere',
    group: 'exploitation',
    asset: 'reservoir',
    waterCapacity: 12_000,
    waterInflowPerHour: 35,
  },
};

/** Paramètres économiques et de simulation (équilibrage). */
export const ECONOMY = {
  startCash: 320_000,
  defaultTicketPrice: 39,
  minTicketPrice: 10,
  maxTicketPrice: 95,
  rentalPrice: 24,
  rentalShare: 0.3,
  electricityPerKwh: 0.19,
  /** Coût d'électricité d'un enneigeur par heure de jeu. */
  cannonPowerPerHour: 18,
  /** Revente partielle à la démolition. */
  demolishRefund: 0.25,
  /** Satisfaction minimale pour qu'un visiteur reste toute la journée. */
  leaveSatisfaction: 25,
} as const;

/**
 * Comptoirs (caisses, location) : file d'attente physique devant la porte,
 * servie au débit du bâtiment. Attentes en secondes de simulation, comme
 * celles des remontées (temps vécu par le visiteur).
 */
export const SERVICE = {
  /** Au-delà de cette attente prévue, le visiteur renonce aux caisses et repart. */
  ticketPatience: 150,
  /** Au-delà de cette attente prévue, le visiteur renonce à louer. */
  rentalPatience: 150,
  /** Attente supportée sans perte de satisfaction. */
  tolerance: 45,
  /** Vente de secours au village quand aucune caisse n'est ouverte (forfaits par heure de jeu). */
  fallbackTicketsPerHour: 60,
} as const;

export const STOCKS = {
  water: { capacity: 15_000, start: 9_000, inflowPerHour: 45 },
  fuel: { capacity: 6_000, start: 3_200 },
  food: { capacity: 2_000, start: 900 },
} as const;

export const SUPPLY_LABEL: Record<SupplyKind, { label: string; unit: string }> = {
  food: { label: 'Denrées', unit: 'repas' },
  fuel: { label: 'Carburant', unit: 'L' },
};

export const MAINTENANCE_LEVELS = [
  { label: 'Réduite', costPerLiftDay: 0, breakdownFactor: 1.7 },
  { label: 'Standard', costPerLiftDay: 400, breakdownFactor: 1 },
  { label: 'Renforcée', costPerLiftDay: 1_100, breakdownFactor: 0.5 },
] as const;

/** Coût d'une remontée selon sa longueur (m). */
export function liftCost(kind: LiftKind, length: number): number {
  const s = LIFTS[kind];
  return Math.round((s.costBase + s.costPerMeter * length) / 100) * 100;
}

/** Prix du forfait jugé « juste » par les visiteurs selon le domaine ouvert. */
export function fairTicketPrice(openKm: number, openLifts: number): number {
  return 22 + 3.2 * openKm + 1.5 * openLifts;
}

/** Débit nominal (personnes par heure). */
export function liftCapacityPerHour(kind: LiftKind): number {
  const s = LIFTS[kind];
  return Math.round((3600 / s.interval) * s.seats);
}
